import MapContext from "./MapContext";
import React from "react";
import styled from "styled-components";

import leaflet from "leaflet";
import "leaflet/dist/leaflet.css"

import geoJsonData from "../data/custom.geo.json";
import SideInfo from "./SideInfo/index.js";
import ModeToggler from "./Icons/ModeToggeler.js";

class MapProvider extends React.Component {
    constructor(props) {
        super(props);
        this.map = React.createRef();
        
        this.mapInstance = null;
        this.currentLayer = null;
        this.geojson = null;
        this.currentTile = null;

        this.state = {
            mode: "light",
            currentCountry: "",
            isSideInfoOpen: false,
            contributions: 0,
            preview: {},
            currentIso: ""
        }
    }

    componentDidMount() {
        const bounds = new leaflet.LatLngBounds(new leaflet.LatLng(100.316708, -185.131836), new leaflet.LatLng(-100.316708, 185.131836));
        this.mapInstance = leaflet.map(this.map.current, {
            center: [35, 90],
            zoom: 3,
            minZoom: 3,
            maxZoom: 5,
            zoomSnap: 0.5,
            zoomControl: false,
            // attributionControl: false,
            maxBounds: bounds,
            maxBoundsViscosity: 1.0,
        });

        this.currentTile = leaflet.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
            attribution: '&copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors',
        }).addTo(this.mapInstance);
        
        const countries = [];
        this.geojson = leaflet.geoJSON(geoJsonData, {
            style: function(feature) {
                if (feature.properties.supported) {
                    return {
                        fillColor: feature.properties.color,
                        weight: 0,
                        fillOpacity: 0.4
                    }
                } else {
                    return {
                        weight: 0,
                        fillColor: "grey",
                    }
                }
            },
            onEachFeature: (feature, layer) => {
                countries.push(feature.properties);                
                
                layer.on({
                    mouseover: (e) => {
                        const layer = e.target;
                        // console.log("left: ", e.originalEvent.clientX, " top: ", e.originalEvent.clientX);
                        if(!feature.properties.supported || feature.properties.contributions === 0) return;
                        this.setState({
                            preview: {
                                currentCountry: feature.properties.name,
                                contributions: feature.properties.contributions,
                                position: {
                                    x: e.originalEvent.clientX,
                                    y: e.originalEvent.clientY
                                },
                                currentIso: feature.properties.iso
                            }
                        })

                        // Don't apply hover styles to the selected country
                        if (this.state.currentCountry === layer.feature.properties.name) return;

                        layer.setStyle({
                            color: feature.properties.color,
                            weight: 3
                        });
                    
                        if (!leaflet.Browser.ie && !leaflet.Browser.opera && !leaflet.Browser.edge) {
                            layer.bringToFront();
                        }
                    },
                    mouseout: (e) => {
                        this.setState({
                            preview: {}
                        })
                        // Don't reset style for the selected country
                        if (this.state.currentCountry === layer.feature.properties.name) return;
                        if(!feature.properties.supported || feature.properties.contributions === 0) return;

                        this.geojson.resetStyle(e.target);
                    },
                    click: (e) => {
                        if(!feature.properties.supported || feature.properties.contributions === 0) return;

                        const layer = e.target;
                        if (this.currentLayer) this.geojson.resetStyle(this.currentLayer);
                        this.currentLayer = null;

                        // we reset the current country first
                        this.setState({
                            currentCountry: "",
                            isSideInfoOpen: false,
                            contributions: 0,
                            preview: {},
                            currentIso: ""
                        }, () => {
                            this.mapInstance.fitBounds(e.target.getBounds());
                            this.setState({
                                currentCountry: feature.properties.name,
                                isSideInfoOpen: true,
                                currentIso: feature.properties.iso,
                                contributions: feature.properties.contributions,
                            })
                            this.currentLayer = layer;
                        })
                    },
                })
            }
        }).addTo(this.mapInstance);

        this.setState({ countries: countries });
    }

    componentDidUpdate(props, prevState) {
        if (prevState.currentCountry === this.state.currentCountry) return;
        if (this.state.currentCountry && this.currentLayer) {
            this.currentLayer.setStyle({
                weight: 5,
                color: this.currentLayer.feature.properties.color,
            })
            if (!leaflet.Browser.ie && !leaflet.Browser.opera && !leaflet.Browser.edge) {
                this.currentLayer.bringToFront();
            }
        }
    }

    changeTile = (mode) => {
        this.mapInstance.removeLayer(this.currentTile);
        if (mode === "light") {
            this.currentTile = leaflet.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
                attribution: '&copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors',
            }).addTo(this.mapInstance);
        } else {
            this.currentTile = leaflet.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
                attribution: '&copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors',
            }).addTo(this.mapInstance);
        }
    }

    handleMode = () => {
        this.setState(state => ({
            mode: state.mode === "light"
                ? "dark"
                : "light"
        }), () => {
           this.changeTile(this.state.mode);
        });
    }

    resetAllSelection = () => {
        this.geojson.resetStyle(this.currentLayer);
        this.currentLayer = null;
        this.setState({
            currentCountry: "",
            currentIso: "",
            isSideInfoOpen: false,
            contributions: 0,
            preview: {}
        });
    }

    render() {
        return <MapContext.Provider value={{ 
            refs: {
                map: this.map
            },
            mode: this.state.mode,
            handleMode: this.handleMode,
            resetAllSelection: this.resetAllSelection,
            isSideInfoOpen: this.state.isSideInfoOpen,
            currentCountry: this.state.currentCountry,
            contributions: this.state.contributions,
            preview: this.state.preview,
            countries: this.state.countries,
            currentIso: this.state.currentIso
         }}>
            { this.props.children }
        </MapContext.Provider>
    }
}

export default MapProvider;